import {
  Box,
  Button,
  Heading,
  VStack,
  Text,
  Flex,
  Stack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  HStack,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faPassport,
  faCalendar,
  faUser,
  faIdBadge,
  faSignature,
  faHashtag,
} from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { formatDateTimeCET } from "../../utils/helpers";
import { setAffiliatePrice } from "../../services/affiliate";
import ReservationDetails from "../reservation-details/ReservationDetails";

const InfoRow = ({ icon, label, value, t }) => (
  <Flex align="center">
    <Box color="teal.600" w="24px" me={3}>
      <FontAwesomeIcon icon={icon} />
    </Box>
    <Text fontSize="sm">
      <strong>{label}:</strong> {value || t("affiliateNotAvailable")}
    </Text>
  </Flex>
);

function ForeignPartnerAffiliatePricePage({
  bookingId,
  bookingSummary,
  foreignPartnerBooking,
}) {
  const { t } = useTranslation("common");
  const location = useLocation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [affiliatePriceAmount, setAffiliatePriceAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isValid = /^\d*\.?\d*$/.test(affiliatePriceAmount);

  const basePrice = foreignPartnerBooking?.booking?.fullPrice;

  useEffect(() => {
    const scrollToPriceForm = () => {
      const el = document.getElementById("set-affiliate-price");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const timer = window.setTimeout(scrollToPriceForm, 400);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  const handleSetPrice = async () => {
    if (Number(affiliatePriceAmount) < Number(basePrice)) {
      toast({
        title: t("affiliatePriceTooLowTitle"),
        description: t("affiliatePriceTooLowDescription", {
          affiliatePrice: affiliatePriceAmount,
          basePrice,
        }),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await setAffiliatePrice(bookingId, affiliatePriceAmount);
      toast({
        title: t("affiliatePriceSetSuccess"),
        description: t("affiliatePriceSetSuccessDescription", {
          price: affiliatePriceAmount,
        }),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      await queryClient.invalidateQueries({
        queryKey: ["get-foreign-partner-booking", bookingId],
      });
    } catch (err) {
      if (err.response?.status === 422) {
        toast({
          title: t("affiliatePriceTooLowServerTitle"),
          description: t("affiliatePriceTooLowServerDescription"),
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("affiliatePriceNotSetTitle"),
          description: t("affiliatePriceNotSetDescription"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack spacing={8} align="stretch" maxW="900px" mx="auto" py={6} px={4}>
      <Box textAlign="center">
        <Heading as="h1" size="lg" color="teal.700" mb={2}>
          {t("affiliatePricePageTitle")}
        </Heading>
        <Text color="gray.600" fontSize="md">
          {t("affiliatePricePageSubtitle")}
        </Text>
      </Box>

      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        {t("affiliatePricePageMandatory")}
      </Alert>

      <ReservationDetails
        bookingSummary={bookingSummary}
        hidePaymentSummary
      />

      <Box
        id="set-affiliate-price"
        borderWidth="2px"
        borderRadius="lg"
        p={6}
        bg="teal.50"
        borderColor="teal.400"
        boxShadow="md"
        scrollMarginTop="24px"
      >
        <Heading size="md" color="teal.700" mb={4}>
          <FontAwesomeIcon icon={faIdBadge} style={{ marginInlineEnd: "8px" }} />
          {t("affiliateClientDetails")}
        </Heading>

        <Stack spacing={3} mb={6}>
          <InfoRow
            icon={faHashtag}
            label={t("affiliateBookingId")}
            value={foreignPartnerBooking.referenceNumber}
            t={t}
          />
          <InfoRow
            icon={faUser}
            label={t("affiliateClientFirstName")}
            value={foreignPartnerBooking.clientFirstName}
            t={t}
          />
          <InfoRow
            icon={faUser}
            label={t("affiliateClientLastName")}
            value={foreignPartnerBooking.clientLastName}
            t={t}
          />
          <InfoRow
            icon={faEnvelope}
            label={t("affiliateEmail")}
            value={foreignPartnerBooking.email}
            t={t}
          />
          <InfoRow
            icon={faPhone}
            label={t("affiliateTelephone")}
            value={foreignPartnerBooking.telephoneNumber}
            t={t}
          />
          <InfoRow
            icon={faSignature}
            label={t("affiliateNameOnPassport")}
            value={foreignPartnerBooking.nameOnPassport}
            t={t}
          />
          <InfoRow
            icon={faPassport}
            label={t("affiliatePassportExpiration")}
            value={foreignPartnerBooking.passportExpirationDate}
            t={t}
          />
          <InfoRow
            icon={faCalendar}
            label={t("affiliateCreatedAt")}
            value={formatDateTimeCET(foreignPartnerBooking.createdAt)}
            t={t}
          />
        </Stack>

        <VStack spacing={4} align="stretch" p={4} bg="white" borderRadius="md">
          <Text color="teal.700" fontWeight="bold" textAlign="center">
            {t("affiliateSetPriceHelp")}
          </Text>

          <HStack
            spacing={6}
            width="100%"
            justify="space-between"
            align={{ base: "stretch", md: "flex-end" }}
            flexDirection={{ base: "column", md: "row" }}
          >
            <Text color="teal.600" fontSize="lg" fontWeight="bold">
              {t("affiliateBasePriceValue", { price: basePrice })}
            </Text>

            <FormControl
              isInvalid={Boolean(affiliatePriceAmount) && !isValid}
              maxW={{ md: "320px" }}
              w="full"
            >
              <FormLabel htmlFor="affiliate-price-input" color="red.500" fontWeight="bold">
                {t("affiliatePrice")} *
              </FormLabel>
              <Input
                id="affiliate-price-input"
                placeholder={t("affiliateEnterPrice")}
                value={affiliatePriceAmount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^[0-9]*\.?[0-9]*$/.test(val)) {
                    setAffiliatePriceAmount(val);
                  }
                }}
                type="text"
                inputMode="decimal"
                size="lg"
                borderColor="teal.500"
                _focus={{ borderColor: "teal.600", boxShadow: "outline" }}
              />
              {!isValid && (
                <FormErrorMessage>{t("affiliatePriceInvalid")}</FormErrorMessage>
              )}
            </FormControl>
          </HStack>

          <Button
            colorScheme="teal"
            size="lg"
            width="full"
            isLoading={isSubmitting}
            isDisabled={!affiliatePriceAmount || !isValid}
            onClick={handleSetPrice}
          >
            {t("affiliateSetPriceAndSubmit")}
          </Button>
        </VStack>
      </Box>
    </VStack>
  );
}

export default ForeignPartnerAffiliatePricePage;
